import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { getGoogleCalendarStatus, getGoogleCalendars, updateChannelCalendars } from '../../api/calendar';


const TOTAL_STEPS = 3;


const CalendarOnboardingPage = () => {
    const { uuid, channelUuid } = useParams();
    const navigate = useNavigate();

    const [단계, set단계] = useState(1);
    const [구글연결됨, set구글연결됨] = useState(false);
    const [캘린더목록, set캘린더목록] = useState([]);
    const [캘린더로딩중, set캘린더로딩중] = useState(true);
    const [쓰기캘린더, set쓰기캘린더] = useState(null);
    const [읽기캘린더목록, set읽기캘린더목록] = useState([]);
    const [저장중, set저장중] = useState(false);

    useEffect(() => {
        getGoogleCalendarStatus()
            .then((데이터) => {
                if (데이터?.connected) {
                    set구글연결됨(true);
                    return getGoogleCalendars().then((목록) => {
                        const 배열 = Array.isArray(목록)
                            ? 목록
                            : Array.isArray(목록?.calendars)
                                ? 목록.calendars
                                : [];
                        set캘린더목록(배열);
                        set단계(2);
                    });
                }
            })
            .catch(() => {})
            .finally(() => {
                set캘린더로딩중(false);
            });
    }, []);

    const 구글연결하기 = () => {
        const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
        const redirectUri = `${window.location.origin}/calendar/google`;
        const scope = [
            'https://www.googleapis.com/auth/calendar',
            'https://www.googleapis.com/auth/calendar.events',
        ].join(' ');
        sessionStorage.setItem('calendarConnectReturnTo', window.location.pathname);
        window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=code&scope=${encodeURIComponent(scope)}&access_type=offline&prompt=consent`;
    };

    const 읽기캘린더토글 = (id) => {
        set읽기캘린더목록((prev) =>
            prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]
        );
    };

    const 다음단계가능 = () => {
        if (단계 === 1) return 구글연결됨;
        if (단계 === 2) return 쓰기캘린더 !== null;
        if (단계 === 3) return 읽기캘린더목록.length > 0;
        return false;
    };

    const 완료하기 = async () => {
        const targetCalendar = 캘린더목록.find((c) => c.id === 쓰기캘린더);
        const sourceCalendars = 캘린더목록.filter((c) => 읽기캘린더목록.includes(c.id));

        const body = {
            target: {
                provider: 'GOOGLE',
                external_id: targetCalendar.id,
                calendar_name: targetCalendar.title,
            },
            sources: sourceCalendars.map((c) => ({
                provider: 'GOOGLE',
                external_id: c.id,
                calendar_name: c.title,
            })),
        };

        set저장중(true);
        try {
            await updateChannelCalendars(uuid, channelUuid, body);
            navigate(`/stores/${uuid}/channels/${channelUuid}`);
        } catch {
            alert('저장 중 오류가 발생했습니다.');
        } finally {
            set저장중(false);
        }
    };

    return (
        <PageWrapper>
            <Card>
                <ProgressBar>
                    {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
                        <ProgressStep key={i} $active={i < 단계} />
                    ))}
                </ProgressBar>

                <StepLabel>단계 {단계} / {TOTAL_STEPS}</StepLabel>

                {단계 === 1 && (
                    <StepView>
                        <StepTitle>구글 계정 연결하기</StepTitle>
                        <StepDesc>
                            구글 계정을 연결하면 캘린더 목록을 불러오고<br />
                            예약을 자동으로 등록할 수 있습니다.
                        </StepDesc>

                        <GoogleConnectArea $connected={false}>
                            {캘린더로딩중 ? (
                                <LoadingText>연결 상태 확인 중...</LoadingText>
                            ) : (
                                <GoogleButton onClick={구글연결하기}>
                                    <GoogleIcon viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                                    </GoogleIcon>
                                    구글 계정으로 연결하기
                                </GoogleButton>
                            )}
                        </GoogleConnectArea>
                    </StepView>
                )}

                {단계 === 2 && (
                    <StepView>
                        <StepTitle>예약을 저장할 캘린더</StepTitle>
                        <StepDesc>
                            새 예약이 들어오면 선택한 캘린더에 자동으로 등록됩니다.<br />
                            하나만 선택해 주세요.
                        </StepDesc>

                        {캘린더로딩중 ? (
                            <LoadingText>캘린더 목록 불러오는 중...</LoadingText>
                        ) : <CalendarList>
                            {캘린더목록.map((캘린더) => (
                                <CalendarItem
                                    key={캘린더.id}
                                    $selected={쓰기캘린더 === 캘린더.id}
                                    onClick={() => set쓰기캘린더(캘린더.id)}
                                >
                                    <CalendarDot $color={캘린더.background_color} />
                                    <CalendarName>{캘린더.title}</CalendarName>
                                    <RadioCircle $selected={쓰기캘린더 === 캘린더.id}>
                                        {쓰기캘린더 === 캘린더.id && <RadioDot />}
                                    </RadioCircle>
                                </CalendarItem>
                            ))}
                        </CalendarList>}
                    </StepView>
                )}

                {단계 === 3 && (
                    <StepView>
                        <StepTitle>내 일정을 가져올 캘린더</StepTitle>
                        <StepDesc>
                            선택한 캘린더의 일정을 확인해 이중 예약을 방지합니다.<br />
                            여러 개 선택할 수 있습니다.
                        </StepDesc>

                        <CalendarList>
                            {캘린더목록.map((캘린더) => (
                                <CalendarItem
                                    key={캘린더.id}
                                    $selected={읽기캘린더목록.includes(캘린더.id)}
                                    onClick={() => 읽기캘린더토글(캘린더.id)}
                                >
                                    <CalendarDot $color={캘린더.background_color} />
                                    <CalendarName>{캘린더.title}</CalendarName>
                                    <Checkbox $selected={읽기캘린더목록.includes(캘린더.id)}>
                                        {읽기캘린더목록.includes(캘린더.id) && <CheckMark>✓</CheckMark>}
                                    </Checkbox>
                                </CalendarItem>
                            ))}
                        </CalendarList>
                    </StepView>
                )}

                <ButtonRow>
                    {단계 > 1 ? (
                        <BackButton onClick={() => set단계((p) => p - 1)}>이전</BackButton>
                    ) : (
                        <BackButton onClick={() => navigate(`/stores/${uuid}/channels/${channelUuid}`)}>
                            취소
                        </BackButton>
                    )}

                    {단계 < TOTAL_STEPS ? (
                        <NextButton
                            disabled={!다음단계가능()}
                            onClick={() => set단계((p) => p + 1)}
                        >
                            다음
                        </NextButton>
                    ) : (
                        <NextButton
                            disabled={!다음단계가능() || 저장중}
                            onClick={완료하기}
                        >
                            {저장중 ? '저장 중...' : '완료'}
                        </NextButton>
                    )}
                </ButtonRow>
            </Card>
        </PageWrapper>
    );
};


const PageWrapper = styled.div`
    width: 100%;
    min-height: 100dvh;
    background: #f7f8fa;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    box-sizing: border-box;
`;

const Card = styled.div`
    width: 100%;
    max-width: 480px;
    background: #ffffff;
    border: 1px solid #eeeeee;
    border-radius: 20px;
    padding: 40px;
    display: flex;
    flex-direction: column;
    gap: 28px;
`;

const ProgressBar = styled.div`
    display: flex;
    gap: 6px;
`;

const ProgressStep = styled.div`
    flex: 1;
    height: 4px;
    border-radius: 2px;
    background: ${({ $active }) => ($active ? '#487BFD' : '#e0e0e0')};
    transition: background 0.3s;
`;

const StepLabel = styled.span`
    font-family: Pretendard;
    font-size: 12px;
    font-weight: 500;
    color: #aaaaaa;
`;

const StepView = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
`;

const StepTitle = styled.h2`
    font-family: Pretendard;
    font-size: 20px;
    font-weight: 700;
    color: #111111;
    margin: 0;
`;

const StepDesc = styled.p`
    font-family: Pretendard;
    font-size: 14px;
    font-weight: 400;
    color: #777777;
    line-height: 170%;
    margin: 0;
`;

const GoogleConnectArea = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    padding: 32px;
    border: 1px solid ${({ $connected }) => ($connected ? '#0F9D58' : '#eeeeee')};
    border-radius: 12px;
    background: ${({ $connected }) => ($connected ? '#e8f5e9' : '#f5f5f5')};
    transition: border-color 0.3s, background 0.3s;
`;

const GoogleButton = styled.button`
    display: flex;
    align-items: center;
    gap: 10px;
    background: #ffffff;
    border: none;
    border-radius: 8px;
    padding: 10px 20px;
    font-family: Pretendard;
    font-size: 14px;
    font-weight: 600;
    color: #212121;
    cursor: pointer;
    transition: opacity 0.15s;

    &:hover {
        opacity: 0.9;
    }
`;

const GoogleIcon = styled.svg`
    width: 18px;
    height: 18px;
`;


const CalendarList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const CalendarItem = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 16px;
    border: 1px solid ${({ $selected }) => ($selected ? '#487BFD' : '#eeeeee')};
    border-radius: 10px;
    background: ${({ $selected }) => ($selected ? '#eef2ff' : '#f7f8fa')};
    cursor: pointer;
    transition: border-color 0.15s, background 0.15s;

    &:hover {
        border-color: #487BFD44;
    }
`;

const CalendarDot = styled.div`
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: ${({ $color }) => $color};
    flex-shrink: 0;
`;

const CalendarName = styled.span`
    flex: 1;
    font-family: Pretendard;
    font-size: 14px;
    font-weight: 500;
    color: #111111;
`;

const RadioCircle = styled.div`
    width: 18px;
    height: 18px;
    border-radius: 50%;
    border: 2px solid ${({ $selected }) => ($selected ? '#487BFD' : '#cccccc')};
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: border-color 0.15s;
`;

const RadioDot = styled.div`
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #487BFD;
`;

const Checkbox = styled.div`
    width: 18px;
    height: 18px;
    border-radius: 4px;
    border: 2px solid ${({ $selected }) => ($selected ? '#487BFD' : '#cccccc')};
    background: ${({ $selected }) => ($selected ? '#487BFD' : 'transparent')};
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: border-color 0.15s, background 0.15s;
`;

const CheckMark = styled.span`
    font-size: 11px;
    color: #ffffff;
    font-weight: 700;
    line-height: 1;
`;

const LoadingText = styled.span`
    font-family: Pretendard;
    font-size: 14px;
    font-weight: 400;
    color: #888888;
    text-align: center;
    padding: 24px 0;
`;

const ButtonRow = styled.div`
    display: flex;
    gap: 8px;
    justify-content: space-between;
`;

const BackButton = styled.button`
    background: none;
    border: 1px solid #eeeeee;
    border-radius: 8px;
    padding: 10px 20px;
    font-family: Pretendard;
    font-size: 14px;
    font-weight: 500;
    color: #777777;
    cursor: pointer;
    transition: border-color 0.15s, color 0.15s;

    &:hover {
        border-color: #bbbbbb;
        color: #333333;
    }
`;

const NextButton = styled.button`
    flex: 1;
    background: #487BFD;
    border: none;
    border-radius: 8px;
    padding: 10px 20px;
    font-family: Pretendard;
    font-size: 14px;
    font-weight: 600;
    color: #ffffff;
    cursor: pointer;
    transition: opacity 0.15s;

    &:hover:not(:disabled) {
        opacity: 0.85;
    }

    &:disabled {
        opacity: 0.35;
        cursor: not-allowed;
    }
`;


export default CalendarOnboardingPage;
